import { useRef, useState } from 'react'
import { CategoryIcon } from '../../../platform/icons/CategoryIcon'
import { useHitTarget } from '../../../platform/responsive/hitTarget'
import { useCategories } from '../useCategories'
import { CategoryPickerDialog, type PickerContext } from './CategoryPickerDialog'
import { setCategoryLabel } from '../setCategoryLabel'

/**
 * Replaces the 285-option `<select>`: a button that names the current category
 * and opens the picker. The accessible name carries both the field and its
 * value ("Category: Groceries"), which is what a `<select>` announced.
 */
export function CategoryField({
  label,
  value,
  onChange,
  context,
  allowNone = false,
  className,
}: {
  label: string
  value: string | null
  onChange: (id: string | null) => void
  context?: PickerContext
  allowNone?: boolean
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const opener = useRef<HTMLButtonElement>(null)
  const categories = useCategories()
  const minHeight = useHitTarget('page')
  const current = value === null || categories.status !== 'success' ? undefined : categories.data.find((c) => c.id === value)
  const set = setCategoryLabel(categories.status, current?.name)
  const name = value === null ? 'No category' : set.name
  const shown = value === null ? 'Choose a category' : set.shown
  return (
    <>
      <button
        ref={opener}
        type="button"
        className={['category-field', className].filter(Boolean).join(' ')}
        style={{ minHeight }}
        aria-haspopup="dialog"
        aria-label={`${label}: ${name}`}
        onClick={() => setOpen(true)}
      >
        {current !== undefined && <CategoryIcon category={current} size={18} />}
        <span className="category-field__name">{shown}</span>
        <span className="category-field__caret" aria-hidden="true">▾</span>
      </button>
      <CategoryPickerDialog
        open={open}
        onClose={() => setOpen(false)}
        value={value}
        onPick={onChange}
        context={context}
        allowNone={allowNone}
        restoreFocusTo={opener}
      />
    </>
  )
}
